/* eslint-disable no-new-wrappers */
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useFavoriteChannelsContext } from 'LocalGluons/components/FavoriteChannelsContext/FavoriteChannelsContext';
import PropTypes from 'prop-types';
import { orderItemsByFavChannelOrder } from 'Utils/epgUtils';
import { useStore } from 'Store';
import { useEpgListMode } from 'LocalGluons/components/EpgListModeContext/EpgListModeContext';
import { areChannelsFavorites } from 'Utils';
import { Device } from 'DeviceUtils';
import {
    DEFAULT_MINI_EPG_LIST_MODE,
    EPG_LIST_TYPES,
    DEFAULT_TV_LIST_WEB_LIMIT,
    DEFAULT_TV_LIST_LIMIT
} from '../../../options';

// TODO: Refactor into TS interface
export const ChannelsServiceContext = createContext({
    channels: [],
    isInitialized: new Boolean(),
    channelsAreLoading: new Boolean(),
    channelsFetchErrored: new Boolean(),
    channelsEndReached: new Boolean(),
    // eslint-disable-next-line no-unused-vars
    initialize: (_params) => {},
    reset: () => {},
    refreshChannels: () => {},
    // eslint-disable-next-line no-unused-vars
    getChannels: (_params, _getOnlyLive, _useCustomOrder, _mergeNewChannels, _forceRefresh) => {},
    // eslint-disable-next-line no-unused-vars
    setChannelData: (_id, _from) => {},
    // eslint-disable-next-line no-unused-vars
    getChannel: (_direction, _channels, _offset = 0) => {},
    lowLatencychannels: []
});

const isEpgModeFavorites = (epgSelectedListMode) =>
    epgSelectedListMode === EPG_LIST_TYPES.FAVORITES;

// Needs deeper refactor
// eslint-disable-next-line max-statements
export const ChannelsServiceContextProvider = ({ children }) => {
    const { appManager } = useStore();
    const { favoriteChannels, setFavoriteChannels } = useFavoriteChannelsContext();
    const { miniEpgSelectedListMode, setMiniEpgSelectedListMode } = useEpgListMode();
    const isInitialized = useRef(false);
    const [currentChannels, setCurrentChannels] = useState([]);
    const [channelsAreLoading, setChannelsAreLoading] = useState(false);
    const [channelsEndReached, setChannelsEndReached] = useState(false);
    const [channelsFetchErrored, setChannelsFetchErrored] = useState(false);
    const availableChannelsTotalCount = useRef(null);
    const channelData = useRef({ id: null, from: null });
    const lastParams = useRef({});
    const nonFilteredChannels = useRef([]);
    const previousMiniEpgSelectedListMode = useRef('');
    const [lowLatencychannels, setLowLatencyChannels] = useState([]);
    const [keptMiniEpgScrollIndex, setKeptMiniEpgScrollIndex] = useState(0);

    const setChannelData = useCallback((id, from) => {
        channelData.current.id = id;
        channelData.current.from = from;
    }, []);

    // Needs deeper refactor
    // eslint-disable-next-line max-statements
    const getChannels = useCallback(
        async (
            params = {
                list: 'live',
                offset: 0,
                limit: 20,
                from: channelData.current.from
            },
            getOnlyLive = false,
            useCustomOrder = false,
            mergeNewChannels = true,
            forceRefresh = false
        ) => {
            if (channelsEndReached && !forceRefresh) {
                if (channelsAreLoading) setChannelsAreLoading(false);
                return [];
            }
            try {
                setChannelsAreLoading(true);
                if (channelsFetchErrored) setChannelsFetchErrored(false);
                // Extend current params to preserve current from
                lastParams.current = { ...params, from: params.from || lastParams.current.from };
                const { offset, limit } = params;
                let newLimit = null;
                if (
                    availableChannelsTotalCount.current !== null &&
                    offset + limit > availableChannelsTotalCount.current
                ) {
                    newLimit = offset + limit - availableChannelsTotalCount.current;
                    setChannelsEndReached(true);
                }
                const { items, totalCount } = await appManager.getApi().fetchChannels(
                    {
                        ...lastParams.current,
                        limit: newLimit || params.limit
                    },
                    getOnlyLive,
                    useCustomOrder,
                    false
                );

                const lowLatencyChannelIds = items
                    .filter((item) => item?.channel?.lowLatency)
                    .map((item) => item?.channel?.channelId);
                setLowLatencyChannels(lowLatencyChannelIds);

                availableChannelsTotalCount.current = totalCount;
                let newChannels = mergeNewChannels ? [...currentChannels, ...items] : items;
                nonFilteredChannels.current = newChannels;
                if (isEpgModeFavorites(miniEpgSelectedListMode)) {
                    newChannels = orderItemsByFavChannelOrder(newChannels, favoriteChannels, true);
                }
                setCurrentChannels(newChannels);
                setChannelsAreLoading(false);
                if (isEpgModeFavorites(miniEpgSelectedListMode)) {
                    setChannelData(favoriteChannels);
                    return favoriteChannels;
                }

                return newChannels;
            } catch (error) {
                console.log(error);
                setChannelsAreLoading(false);
                setChannelsFetchErrored(true);
            }

            return [];
        },
        [
            setChannelData,
            setChannelsAreLoading,
            setChannelsFetchErrored,
            setChannelsEndReached,
            setCurrentChannels,
            miniEpgSelectedListMode,
            favoriteChannels,
            currentChannels,
            channelsEndReached,
            channelsAreLoading
        ]
    );

    const getChannel = useCallback(
        async (direction = -1, channels, offset = 0) => {
            const currentIndex =
                offset +
                channels.findIndex((item) => {
                    const { channelId } = item.channel;
                    return channelId === channelData.current.id;
                });

            let nextChannelIndex = 0;
            if (currentIndex >= 0) nextChannelIndex = currentIndex + direction;
            if (nextChannelIndex < 0) return channels?.[channels.length - 1];

            const nextChannel = channels[nextChannelIndex];
            // Disable consistent return check due to recursion call when we don't have enough channels
            // eslint-disable-next-line consistent-return
            if (nextChannel) return nextChannel;
            // eslint-disable-next-line consistent-return
            if (channelsEndReached || areChannelsFavorites(channels, favoriteChannels))
                return channels[0];

            const newChannels = await getChannels({
                ...lastParams.current,
                offset: channels.length,
                limit: Device.isWeb ? DEFAULT_TV_LIST_WEB_LIMIT : DEFAULT_TV_LIST_LIMIT
            });
            // eslint-disable-next-line consistent-return
            return getChannel(direction, newChannels);
        },
        [getChannels]
    );

    const refreshChannels = useCallback(() => {
        setChannelsAreLoading(true);
        setCurrentChannels([]);
        setChannelsEndReached(false);
        return getChannels(
            {
                ...lastParams.current,
                offset: 0,
                limit: availableChannelsTotalCount.current
            },
            undefined,
            undefined,
            false,
            true
        );
    }, [setChannelsAreLoading, setCurrentChannels, setChannelsEndReached, getChannels]);

    const reset = () => {
        setCurrentChannels([]);
        setChannelsAreLoading(false);
        setChannelsFetchErrored(false);
        setChannelsEndReached(false);
        isInitialized.current = false;
        previousMiniEpgSelectedListMode.current = '';
    };

    const correctDefaultEpgListTypeByCurrentChannel = () => {
        // If current channel is not in favorites, we have no channel to highlight so switch favorites filtering off
        const channelIsInFavorites = favoriteChannels.find(
            ({ channelId }) => channelId === channelData.current.id
        );
        const favoritesListIsSelected = miniEpgSelectedListMode === EPG_LIST_TYPES.FAVORITES;
        if (!channelIsInFavorites && favoritesListIsSelected) {
            setMiniEpgSelectedListMode(DEFAULT_MINI_EPG_LIST_MODE);
        }
    };

    const initialize = async (params) => {
        if (isInitialized.current) return;
        reset();
        correctDefaultEpgListTypeByCurrentChannel();
        await setFavoriteChannels();
        await getChannels(params);
        isInitialized.current = true;
    };

    useEffect(() => {
        if (
            previousMiniEpgSelectedListMode.current === miniEpgSelectedListMode ||
            !currentChannels?.length ||
            !favoriteChannels?.length
        )
            return;

        if (isEpgModeFavorites(miniEpgSelectedListMode)) {
            const orderedChannels = orderItemsByFavChannelOrder(
                currentChannels,
                favoriteChannels,
                true
            );
            setCurrentChannels(orderedChannels);
        } else {
            setCurrentChannels(nonFilteredChannels.current);
        }

        previousMiniEpgSelectedListMode.current = miniEpgSelectedListMode;
    }, [miniEpgSelectedListMode, currentChannels, favoriteChannels]);

    return (
        <ChannelsServiceContext.Provider
            value={{
                isInitialized,
                channels: currentChannels,
                channelsAreLoading,
                setChannelsAreLoading,
                channelsFetchErrored,
                channelsEndReached,
                initialize,
                reset,
                refreshChannels,
                getChannels,
                setChannelData,
                getChannel,
                lowLatencychannels,
                keptMiniEpgScrollIndex,
                setKeptMiniEpgScrollIndex
            }}
        >
            {children}
        </ChannelsServiceContext.Provider>
    );
};

ChannelsServiceContextProvider.defaultProps = {
    children: []
};

ChannelsServiceContextProvider.propTypes = {
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node])
};

export const useLiveChannelsService = () => useContext(ChannelsServiceContext);

export const withLiveChannelsService = (Component) => (props) => (
    <ChannelsServiceContext.Consumer>
        {(channelsServiceContext) => <Component {...props} {...channelsServiceContext} />}
    </ChannelsServiceContext.Consumer>
);
